"use client";

import dynamic from "next/dynamic";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";

import type { KundaliFormCardProps } from "./KundaliFormCard";
import { KUNDALI_NAME_CWV_PROBE } from "./KundaliFormCard.cwv";
import { getLastReadingSnapshot, subscribeLastReading } from "@/lib/last-reading";
import type { KundaliField } from "@/lib/analytics-kundali";

const DEFAULT_DEFER_MS = 3600;
const FORM_EVENTS = ["pointerdown", "keydown", "touchstart", "focusin"] as const;

// rule 58 #3 — the chunk is warmed independently of activation, then `activate`
// flips `ready` only after the import resolves. The passive timer must never
// replace the compact semantic shell with the taller real form without intent.
const loadKundaliFormCard = () => import("./KundaliFormCard");

const KundaliFormCard = dynamic(loadKundaliFormCard, {
  ssr: false,
  loading: () => <KundaliFormShellReserve />,
});

// Bundle-size discipline (2026-07-16 trim pass): the returning-seeker card
// (ContinueConsultationCard stack) renders only when browser-local reading
// memory exists, which is discovered after hydration — so it loads as a
// dynamic chunk instead of shipping in every route that embeds the form card.
// Its reserve mirrors the journey's own declared min height (rule 58 #1), not a
// separate hand-set number that can drift away from it.
const HomeReturningJourney = dynamic(() => import("./HomeReturningJourney"), {
  ssr: false,
  loading: () => <HomeReturningJourneyReserve />,
});

type DeferredKundaliFormCardProps = KundaliFormCardProps & {
  deferMs?: number;
  interactionOnly?: boolean;
  fallbackTitle?: string;
  fallbackBody?: string;
  fallbackSubmitLabel?: string;
  /** Replace the new-kundali form with the remembered consultation on home. */
  preferRememberedJourney?: boolean;
};

export default function DeferredKundaliFormCard({
  deferMs = DEFAULT_DEFER_MS,
  interactionOnly = false,
  fallbackTitle,
  fallbackBody,
  fallbackSubmitLabel,
  preferRememberedJourney = false,
  ...formProps
}: DeferredKundaliFormCardProps) {
  const [ready, setReady] = useState(false);
  const [pendingFocusField, setPendingFocusField] = useState<KundaliField>();
  const preload = useCallback(() => {
    void loadKundaliFormCard().catch(() => undefined);
  }, []);
  // Resolve the chunk first, THEN swap. Only an interaction with the semantic
  // shell calls this function; passive time/global activity can warm the chunk
  // but can never mutate the form slot.
  const activate = useCallback(() => {
    const swap = () => setReady(true);
    void loadKundaliFormCard().then(swap, swap);
  }, []);
  const activateField = useCallback(
    (field: KundaliField) => {
      setPendingFocusField(field);
      activate();
    },
    [activate]
  );
  const rememberedReading = useSyncExternalStore(
    subscribeLastReading,
    getLastReadingSnapshot,
    () => null
  );

  useEffect(() => {
    if (ready) return;

    const timer = interactionOnly ? null : window.setTimeout(preload, deferMs);
    const options: AddEventListenerOptions = { once: true, passive: true };
    for (const eventName of FORM_EVENTS) {
      window.addEventListener(eventName, preload, options);
    }

    return () => {
      if (timer !== null) window.clearTimeout(timer);
      for (const eventName of FORM_EVENTS) {
        window.removeEventListener(eventName, preload);
      }
    };
  }, [deferMs, interactionOnly, preload, ready]);

  if (preferRememberedJourney && rememberedReading) {
    return <HomeReturningJourney reading={rememberedReading} />;
  }

  if (ready) return <KundaliFormCard {...formProps} initialFocusField={pendingFocusField} />;

  return (
    <KundaliFormShell
      containerClassName={formProps.containerClassName}
      title={fallbackTitle}
      body={fallbackBody}
      submitLabel={fallbackSubmitLabel ?? formProps.submitLabel}
      language={formProps.defaultLanguage ?? "en"}
      onActivate={activate}
      onActivateField={activateField}
    />
  );
}

const noop = () => {};

export function KundaliFormShell({
  containerClassName = "mx-auto max-w-md px-4 sm:px-6 pb-16",
  title = "Generate your free janam kundali",
  body = "Share your birth details to unlock chart-grounded dosha, dasha, and upay guidance.",
  submitLabel = "Continue",
  language = "en",
  onActivate = noop,
  onActivateField = noop,
}: {
  containerClassName?: string;
  title?: string;
  body?: string;
  submitLabel?: string;
  // Defaults exist so the shell can double as its own dynamic-import reserve,
  // where next/dynamic's module-scope `loading` cannot receive props.
  language?: "en" | "hi";
  onActivate?: () => void;
  onActivateField?: (field: KundaliField) => void;
}) {
  const shellRef = useRef<HTMLDivElement>(null);
  const copy =
    language === "hi"
      ? {
          eyebrow: "Free Vedic Kundali",
          name: "Naam",
          birthDate: "Janam ki date",
          birthTime: "Janam ka time",
          birthPlace: "Janam ka place",
          phone: "WhatsApp number (required)",
          noSignup: "Saved Kundali aur returning access ke liye. Marketing opt-in alag hai.",
        }
      : {
          eyebrow: "Free Vedic chart",
          name: "Name",
          birthDate: "Birth date",
          birthTime: "Birth time",
          birthPlace: "Birth place",
          phone: "WhatsApp number (required)",
          noSignup: "For your saved Kundali and returning access. Marketing consent is separate.",
        };
  const activateTargetField = useCallback(
    (target: EventTarget | null) => {
      const field = target instanceof Element ? target.closest<HTMLElement>("[data-field]") : null;
      if (field?.dataset.field) {
        onActivateField(field.dataset.field as KundaliField);
        return;
      }
      onActivate();
    },
    [onActivate, onActivateField]
  );

  // The shell is server-rendered so it remains useful during a cold client
  // boot. A seeker can focus a field before React hydrates; that native focus
  // cannot be replayed reliably because it happened before the handler existed.
  // Recover the already-focused field at hydration so the first interaction is
  // never consumed by the shell and a second tap is never required (rule 61).
  useEffect(() => {
    for (const field of shellRef.current?.querySelectorAll<HTMLElement>("[data-field]") ?? []) {
      field.dataset.shellHydrated = "true";
    }
    const activeElement = document.activeElement;
    if (activeElement && shellRef.current?.contains(activeElement)) {
      activateTargetField(activeElement);
    }
  }, [activateTargetField]);

  return (
    <div
      ref={shellRef}
      className={containerClassName}
      onPointerDown={(event) => activateTargetField(event.target)}
      onFocus={(event) => activateTargetField(event.target)}
    >
      <form
        aria-busy="true"
        className="card-sacred rounded-2xl p-5 sm:p-6"
        onSubmit={(event) => {
          event.preventDefault();
          onActivate();
        }}
      >
        <p
          className="mb-1 text-[12px] font-semibold uppercase tracking-[0.18em]"
          style={{ color: "var(--ish-accent)" }}
        >
          {copy.eyebrow}
        </p>
        <h2 className="text-[22px] font-bold leading-tight" style={{ color: "var(--ish-dark)" }}>
          {title}
        </h2>
        <p className="mt-2 text-[13px] leading-relaxed" style={{ color: "var(--ish-text-muted)" }}>
          {body}
        </p>
        <div className="mt-5 grid gap-3">
          <ShellField field="name" label={copy.name} type="text" autoComplete="name" />
          <ShellField field="birthDate" label={copy.birthDate} type="date" autoComplete="bday" />
          <ShellField field="birthTime" label={copy.birthTime} type="time" />
          <ShellField
            field="birthPlace"
            label={copy.birthPlace}
            type="text"
            autoComplete="address-level2"
          />
          <ShellField field="phone" label={copy.phone} type="tel" autoComplete="tel" required />
        </div>
        <button
          type="submit"
          className="btn-sacred mt-5 w-full rounded-lg py-3.5 text-[14px] font-semibold"
        >
          {submitLabel}
        </button>
        <p className="mt-3 text-center text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
          {copy.noSignup}
        </p>
      </form>
    </div>
  );
}

/**
 * Reserve for the form chunk. It renders the SAME shell it stands in for, so the
 * reserved height is the shell's height by construction (rule 58 #1) — the old
 * hand-set `min-h-[420px]` box was ~180px SHORTER than the five-field shell, so
 * every activation shrank the slot and then grew it again.
 */
export function KundaliFormShellReserve() {
  return (
    <div aria-hidden>
      <KundaliFormShell />
    </div>
  );
}

/** Reserve for the returning-journey chunk — mirrors its own declared min height. */
export function HomeReturningJourneyReserve() {
  return (
    <div className="mx-auto min-h-[480px] max-w-md px-4 pb-16 sm:px-6" aria-hidden>
      <div className="card-sacred min-h-[480px] rounded-2xl p-5 sm:p-6" />
    </div>
  );
}

function ShellField({
  field,
  label,
  type,
  autoComplete,
  required = false,
}: {
  field: KundaliField;
  label: string;
  type: "text" | "date" | "time" | "tel";
  autoComplete?: string;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span
        className="mb-1 block text-[12px] font-medium"
        style={{ color: "var(--ish-text-muted)" }}
      >
        {label}
      </span>
      <input
        type={type}
        name={`kundali-shell-${field}`}
        autoComplete={autoComplete}
        required={required}
        readOnly
        data-field={field}
        data-shell-hydrated="false"
        data-cwv-interaction-activate={field === "name" ? KUNDALI_NAME_CWV_PROBE.marker : undefined}
        aria-label={`${label} — open form`}
        className="block h-11 w-full cursor-text rounded-lg px-3"
        style={{
          background: "var(--ish-bg)",
          border: "1px solid var(--ish-border)",
        }}
      />
    </label>
  );
}
